/**
 * Chrome overrides for the "popped field is the chrome" editing treatment,
 * moved verbatim from `lib/base/table/components/editable-cell.tsx` and
 * parameterised by density so non-table surfaces (overview panel, pipeline
 * cards) can reuse the same selector-specificity workarounds without the
 * table-grid-only `min-w-(--cell-field-width)` rule.
 */

import { cx } from '@/utils/cx';

import type { EditorKind } from './editorKinds';

/** 'xs' is the table. 'sm' is the overview panel and pipeline cards. */
export type InlineEditDensity = 'xs' | 'sm';

/**
 * The kind-independent half of the bare-text neutralisation: strip the base
 * Input's wrapper chrome (react-aria Group ring/shadow/bg) and hide the
 * leading property-type icon. Exported so title editors (which need their own
 * padding/typography on the input) can compose it without duplicating the
 * selector strings.
 */
export const FIELD_CHROME_NEUTRALIZE = cx(
  '[&_[role=presentation]]:bg-transparent [&_[role=presentation]]:shadow-none [&_[role=presentation]]:ring-0',
  // Leading property-type icon (absolutely positioned, pointer-events-none)
  '[&_.pointer-events-none.absolute]:hidden'
);

// Single-line editors that render a bare <input> through the base Input
// component (TextField → InputBase → react-aria Group). For these, the popped
// field itself is the chrome, so the inner form-field chrome is neutralised
// (see EDITOR_CHROME_OVERRIDES). Other editors — select, date, phone,
// multi-value — keep their own triggers/cards inside the field.
const EDITOR_CHROME_OVERRIDES = cx(
  FIELD_CHROME_NEUTRALIZE,
  // Padding only — the cell's type size comes from `size='xs'` on the
  // PropertyInput below, not from reaching in by selector.
  '[&_input]:p-0',
  // Grow with content from the cell's inner width (Attio behaviour).
  // Browsers without field-sizing keep the cell width — graceful degradation.
  '[&_input]:w-auto [&_input]:min-w-(--cell-field-width) [&_input]:[field-sizing:content]'
);

// 'sm' density variant of EDITOR_CHROME_OVERRIDES: same ring/shadow/icon
// rules, padding bumped one step (0 -> 0.5), and `min-w-(--cell-field-width)`
// dropped — it's a table-cell grid variable with no meaning off-grid.
const EDITOR_CHROME_OVERRIDES_SM = cx(
  FIELD_CHROME_NEUTRALIZE,
  '[&_input]:p-0.5',
  '[&_input]:w-auto [&_input]:[field-sizing:content]'
);

// PhoneInput renders through InputGroup, whose ring/shadow chrome sits on a
// div[data-input-size] wrapper conditioned on :has(select) — with a
// focus-within ring at even higher specificity — so these overrides use `!`
// to win regardless. Inside the popped field, the field is the chrome.
const PHONE_CHROME_OVERRIDES = cx(
  '[&_div[data-input-size]]:ring-0! [&_div[data-input-size]]:shadow-none! [&_div[data-input-size]]:bg-transparent!'
);

// Card-mode (multi-value) editors: the value rows and the add input read as
// bare text lines — the card is the chrome (Attio treatment). Covers both
// Input-based rows (role=presentation) and phone rows (div[data-input-size]).
// Unlike EDITOR_CHROME_OVERRIDES there is no field-sizing/min-width: rows
// share their line with a drag handle and a remove button and must not grow.
const CARD_EDITOR_CHROME_OVERRIDES = cx(
  // Leading property-type icons would repeat on every row — the column
  // header already names the property. With them hidden, drop the icon
  // padding so values start at the row edge.
  FIELD_CHROME_NEUTRALIZE,
  PHONE_CHROME_OVERRIDES,
  // Compact rows: even the `xs` step's vertical padding leaves each line
  // taller than the card wants (~28-32px). Padding only — type comes from
  // `size='xs'` on the PropertyInput.
  '[&_input]:pl-1 [&_input]:py-1',
  // Multi-select cards: the add-row select trigger is bare like every other
  // add-row editor. Row action buttons (drag/remove/add) carry no ring or
  // shadow, so these are no-ops for them, and their hover states win on
  // specificity.
  '[&_button]:ring-0! [&_button]:shadow-none! [&_button]:bg-transparent',
  // The phone country select fills the input-group height (NativeSelect keeps
  // an unbroken h-full chain inside a group); pin it to the card's denser
  // rhythm. The `!` flags out-rank that h-full chain, PhoneInput's own
  // `!pr-4`, and the in-data-input-wrapper padding rules. Type is NOT set
  // here — the select and the dial code both follow `size='xs'`.
  '[&_select]:h-8! [&_select]:pl-1!',
  // Tighten the select↔dial-code gap inside the leading section.
  '[&_[data-leading]>div]:gap-1'
);

// 'sm' density variant of CARD_EDITOR_CHROME_OVERRIDES: same ring/shadow/bg
// and icon-hiding rules, padding bumped one step (1 -> 1.5).
const CARD_EDITOR_CHROME_OVERRIDES_SM = cx(
  FIELD_CHROME_NEUTRALIZE,
  PHONE_CHROME_OVERRIDES,
  '[&_input]:pl-1.5 [&_input]:py-1.5',
  '[&_button]:ring-0! [&_button]:shadow-none! [&_button]:bg-transparent',
  '[&_select]:h-8! [&_select]:pl-1.5!',
  '[&_[data-leading]>div]:gap-1'
);

// Enumerated pickers (select/boolean) render a button trigger through the
// base Select. Inside the popped field the trigger's standalone chrome
// (ring/shadow/bg — SELECT_TRIGGER_BASE, plus its focus ring) is redundant:
// the field is the chrome and the options open immediately. Hover/active
// styling on menu items is unaffected (their rules carry higher specificity).
const PICKER_CHROME_OVERRIDES = cx(
  '[&_button]:ring-0! [&_button]:shadow-none! [&_button]:bg-transparent',
  // The trigger value must sit exactly where the cell's label sat: the
  // popped field already provides the 13px content padding, so the
  // trigger's own inner padding (on the SelectValue span) is dropped...
  '[&_button>span]:p-0',
  // ...the leading property-type glyph is hidden (the column header already
  // names the field; the icon just shoves the value rightwards)...
  '[&_button_[data-icon]]:hidden',
  // ...and the value renders at the cell's regular weight, not the
  // standalone trigger's font-medium, so entering edit mode doesn't make
  // the text jump bolder.
  '[&_button_p]:font-normal'
);

// 'sm' density variant of PICKER_CHROME_OVERRIDES: same ring/shadow, icon-
// hiding, and font-weight rules, padding bumped one step (0 -> 0.5).
const PICKER_CHROME_OVERRIDES_SM = cx(
  '[&_button]:ring-0! [&_button]:shadow-none! [&_button]:bg-transparent',
  '[&_button>span]:p-0.5',
  '[&_button_[data-icon]]:hidden',
  '[&_button_p]:font-normal'
);

// Kinds whose overlay is a card (or an association picker) and therefore
// never gets the table-grid `min-w-(--cell-field-width)` clamp — mirrors
// `!isCardEditor && !isAssociationEditor` in editable-cell.tsx.
const MIN_WIDTH_EXCLUDED_KINDS = new Set<EditorKind>([
  'multi-value-card',
  'single-association',
  'multi-association',
]);

/**
 * Chrome overrides for a given editor kind at a given density. For `'xs'`
 * this reproduces exactly what editable-cell.tsx composes today (see
 * editable-cell.tsx:1055-1064) — cx() may reorder classes, but the resulting
 * rule set is equivalent. For `'sm'` the same ring/shadow/icon-hiding rules
 * apply with padding varied and the table-only min-width clamp omitted.
 */
export function getChromeOverrides(
  kind: EditorKind,
  density: InlineEditDensity
): string {
  const isXs = density === 'xs';
  return cx(
    isXs && !MIN_WIDTH_EXCLUDED_KINDS.has(kind) && 'min-w-(--cell-field-width)',
    kind === 'bare-text' &&
      (isXs ? EDITOR_CHROME_OVERRIDES : EDITOR_CHROME_OVERRIDES_SM),
    kind === 'phone' && PHONE_CHROME_OVERRIDES,
    kind === 'picker' &&
      (isXs ? PICKER_CHROME_OVERRIDES : PICKER_CHROME_OVERRIDES_SM),
    kind === 'multi-value-card' &&
      (isXs ? CARD_EDITOR_CHROME_OVERRIDES : CARD_EDITOR_CHROME_OVERRIDES_SM)
  );
}
